import { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DataContext from "../../../context/DataContext";
import { useRequest } from "../../../hooks";
import { ToolsRunLineItemProps } from "../../../interfaces/interfaces";

type NamedOption = { id: number; name: string };
type ProductOption = NamedOption & { partNumber: string };
type FreeOption<T> = T | string | null;

interface Props {
  row: ToolsRunLineItemProps | null;
  onClose: () => void;
  onResolved: () => void;
}

const labelFor = <T extends NamedOption>(option: T | string) => typeof option === "string" ? option : option.name;
const idFor = <T extends NamedOption>(option: FreeOption<T>) => typeof option === "object" && option ? option.id : undefined;
const creationFor = <T extends NamedOption>(option: FreeOption<T>) => typeof option === "string" && option.trim() ? { name: option.trim() } : undefined;

export const ToolsRunResolveDialog = ({ row, onClose, onResolved }: Props) => {
  const { handleRequest } = useRequest();
  const {
    clientOptions,
    wellOptions,
    productOptions,
    fetchDataWells,
  } = useContext(DataContext);
  const clients = clientOptions as NamedOption[];
  const wells = wellOptions as NamedOption[];
  const products = productOptions as ProductOption[];
  const [selectedClient, setSelectedClient] = useState<FreeOption<NamedOption>>(null);
  const [selectedWell, setSelectedWell] = useState<FreeOption<NamedOption>>(null);
  const [selectedProduct, setSelectedProduct] = useState<FreeOption<ProductOption>>(null);
  const [wellInput, setWellInput] = useState("");
  const [productInput, setProductInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    const clientId = row.clientId || row.suggestedClientId;
    const client = clients.find((option) => option.id === clientId) || null;
    setSelectedClient(client);
    setSelectedWell(null);
    setWellInput("");
    setProductInput("");
    setSelectedProduct(products.find((option) => option.id === (row.productId || row.suggestedProductIds?.[0])) || null);
    if (clientId) fetchDataWells(clientId);
    setError("");
  // DataContext actions are not referentially stable; this effect intentionally initializes per row.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  useEffect(() => {
    if (!row || selectedWell) return;
    const wellId = row.wellId || row.suggestedWellId;
    const well = wells.find((option) => option.id === wellId);
    if (well) setSelectedWell(well);
  }, [row, wells, selectedWell]);

  const suggestionText = useMemo(() => row?.reviewReason?.split(",").map((reason) => reason.replaceAll("_", " ")).join(" · ") || "", [row]);

  const changeClient = (value: FreeOption<NamedOption>) => {
    setSelectedClient(value);
    setSelectedWell(null);
    const id = idFor(value);
    if (id) fetchDataWells(id);
  };

  const effectiveWell =
    selectedWell || (wellInput.trim() ? wellInput.trim() : null);
  const effectiveProduct =
    selectedProduct || (productInput.trim() ? productInput.trim() : null);

  const submit = async () => {
    if (!row) return;
    setBusy(true);
    setError("");
    const createProduct = creationFor(effectiveProduct);
    const payload = {
      clientId: idFor(selectedClient),
      wellId: idFor(effectiveWell),
      productId: idFor(effectiveProduct),
      createClient: creationFor(selectedClient),
      createWell: creationFor(effectiveWell),
      createProduct: createProduct ? { ...createProduct, partNumber: row.partNumber } : undefined,
    };
    try {
      const response = await handleRequest({
        endpoint: `tools-run/${row.id}/resolve`,
        options: { method: "PUT", body: JSON.stringify(payload) },
      });
      if (!response.success) throw new Error(response.error || "The row could not be resolved");
      onResolved();
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "The row could not be resolved");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850 }}>Resolve shipment match</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2, mb: 2.5, bgcolor: "#F7F6F1", border: "1px solid #E9E6DB", borderRadius: 2 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={800}>Source row</Typography>
          <Typography fontWeight={750}>{row?.rawShipToClient || "—"} <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>/ {row?.rawShipToWell || "—"}</Box></Typography>
          <Typography variant="body2" color="text.secondary">{row?.rawItem || "—"} · Invoice {row?.invoice || "—"}</Typography>
          {suggestionText ? <Typography variant="caption" sx={{ color: "#9A6200", display: "block", mt: 0.5, textTransform: "capitalize" }}>{suggestionText}</Typography> : null}
        </Box>
        <Autocomplete
          freeSolo
          value={selectedClient}
          options={clients}
          getOptionLabel={labelFor}
          onChange={(_, value) => changeClient(value)}
          renderInput={(params) => <TextField {...params} label="Client" helperText="Select a match or type a new client name" margin="normal" />}
        />
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={selectedWell}
          options={wells}
          getOptionLabel={labelFor}
          onChange={(_, value) => {
            setSelectedWell(value);
            if (typeof value === "string") setWellInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setWellInput(value);
            if (reason === "input") setSelectedWell(null);
          }}
          disabled={!selectedClient}
          renderInput={(params) => <TextField {...params} label="Well" helperText="Select an existing well, or type a new well name to create it when this match is saved." margin="normal" />}
        />
        <Divider sx={{ my: 1 }} />
        <Autocomplete
          freeSolo
          selectOnFocus
          clearOnBlur={false}
          value={selectedProduct}
          options={products}
          getOptionLabel={(option) => typeof option === "string" ? option : `${option.partNumber} · ${option.name}`}
          onChange={(_, value) => {
            setSelectedProduct(value);
            if (typeof value === "string") setProductInput(value);
          }}
          onInputChange={(_, value, reason) => {
            setProductInput(value);
            if (reason === "input") setSelectedProduct(null);
          }}
          renderInput={(params) => <TextField {...params} label="Tool" helperText={`Source part number: ${row?.partNumber || "—"}. Select a product or type a new tool name.`} margin="normal" />}
        />
        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={busy || !selectedClient || !effectiveWell || !effectiveProduct} onClick={submit} startIcon={<AutoFixHighRoundedIcon />}>
          Save match
        </Button>
      </DialogActions>
    </Dialog>
  );
};
